// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0

import {
  useActiveSpeakersState,
  useAudioVideo,
  useContentShareState,
  useLocalVideo,
  useMeetingManager,
  useRosterState,
} from "amazon-chime-sdk-component-library-react";
import {
  AudioVideoObserver,
  VideoDownlinkObserver,
  VideoSource,
} from "amazon-chime-sdk-js";
import React, {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useCallback,
} from "react";
import { Layout } from "../../types";
import { useAppState } from "../AppStateProvider";
import { usePlatformStore } from "@platform/store";
import { useVideoDispatch, ADD_ACTIVE_SPEAKER, REMOVE_ACTIVE_SPEAKER, CLEAR_ACTIVE_SPEAKERS } from "../VideoContext";
import {
  Controls,
  initialState,
  reducer,
  State,
  VideoTileGridAction,
} from "./state";
import { videoEventBus } from "../../videoEventBus";

const VideoTileGridStateContext = createContext<State | undefined>(undefined);
const VideoTileGridControlContext = createContext<Controls | undefined>(
  undefined
);

const VideoTileGridProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { priorityBasedPolicy } = useAppState();
  const meetingManager = useMeetingManager();
  const audioVideo = useAudioVideo();
  const activeSpeakers = useActiveSpeakersState();
  const { roster } = useRosterState();
  const { layout, setLayout } = useAppState();
  const { isVideoEnabled } = useLocalVideo();
  const { isLocalUserSharing, sharingAttendeeId } = useContentShareState();
  const [state, dispatch] = useReducer(reducer, initialState);

  const user = usePlatformStore((s) => s.user);
  const videoDispatch = useVideoDispatch();
  const previousActiveSpeakersRef = useRef<string[]>([]);

  useEffect(() => {
    dispatch({
      type: VideoTileGridAction.UpdateAttendeeStates,
      payload: { roster },
    });
  }, [roster]);

  useEffect(() => {
    dispatch({
      type: VideoTileGridAction.UpdateActiveSpeakers,
      payload: {
        activeSpeakers,
      },
    });

    // Track active speaker changes for analytics
    const previousSpeakers = previousActiveSpeakersRef.current;
    const currentSpeakers = activeSpeakers;

    // Helper function to get user ID from attendee ID
    const getUserIdFromAttendeeId = (attendeeId: string): string => {
      const attendee = roster[attendeeId];
      
      // If this is the local user, we can get the user ID from context
      if (attendeeId === meetingManager.meetingSession?.configuration.credentials?.attendeeId) {
        return user?.id?.toString() || attendeeId;
      }
      
      // For remote users, try to extract user info from externalUserId
      if (attendee?.externalUserId) {
        // The externalUserId format is: firstName-lastName-TS-timestamp
        // We can't get the actual user ID from this, so we'll use a hash of the name
        // This provides a consistent identifier for analytics
        const namePart = attendee.externalUserId.split('-TS-')[0];
        const nameHash = namePart.replace('-', '_'); // Convert to a valid identifier
        return nameHash;
      }
      
      // Fallback to attendee ID if we can't get any user info
      return attendeeId;
    };

    // Find new active speakers
    const newSpeakers = currentSpeakers.filter(speaker => !previousSpeakers.includes(speaker));
    newSpeakers.forEach(speaker => {
      const userId = getUserIdFromAttendeeId(speaker);
      videoEventBus.emitEvent("videochat:active:speaker:started", { userId });
      
      // Update VideoContext with new active speaker
      videoDispatch({
        type: ADD_ACTIVE_SPEAKER,
        payload: { attendeeId: speaker }
      });
    });

    // Find speakers who stopped being active
    const stoppedSpeakers = previousSpeakers.filter(speaker => !currentSpeakers.includes(speaker));
    stoppedSpeakers.forEach(speaker => {
      const userId = getUserIdFromAttendeeId(speaker);
      videoEventBus.emitEvent("videochat:active:speaker:stopped", { userId });
      
      // Update VideoContext by removing stopped speaker
      videoDispatch({
        type: REMOVE_ACTIVE_SPEAKER,
        payload: { attendeeId: speaker }
      });
    });

    // Update the ref for next comparison
    previousActiveSpeakersRef.current = currentSpeakers;
  }, [activeSpeakers, videoDispatch]);

  // Cleanup active speakers when component unmounts
  useEffect(() => {
    return () => {
      videoDispatch({
        type: CLEAR_ACTIVE_SPEAKERS,
      });
    };
  }, [videoDispatch]);

  useEffect(() => {
    if (!audioVideo) {
      return;
    }

    const localAttendeeId =
      meetingManager.meetingSession?.configuration.credentials?.attendeeId ||
      null;

    const observer: AudioVideoObserver = {
      remoteVideoSourcesDidChange: (videoSources: VideoSource[]): void => {
        dispatch({
          type: VideoTileGridAction.UpdateVideoSources,
          payload: {
            videoSources,
            localAttendeeId,
          },
        });
      },
    };

    audioVideo.addObserver(observer);

    return (): void => audioVideo.removeObserver(observer);
  }, [audioVideo]);

  useEffect(() => {
    if (!audioVideo || !priorityBasedPolicy) {
      return;
    }

    const observer: VideoDownlinkObserver = {
      tileWillBePausedByDownlinkPolicy: (tileId: number): void => {
        const attendeeId = audioVideo
          .getVideoTile(tileId)
          ?.state().boundAttendeeId;
        if (attendeeId) {
          dispatch({
            type: VideoTileGridAction.PauseVideoTile,
            payload: { attendeeId },
          });
        }
      },
      tileWillBeUnpausedByDownlinkPolicy: (tileId: number): void => {
        const attendeeId = audioVideo
          .getVideoTile(tileId)
          ?.state().boundAttendeeId;
        if (attendeeId) {
          dispatch({
            type: VideoTileGridAction.UnpauseVideoTile,
            payload: { attendeeId },
          });
        }
      },
    };

    priorityBasedPolicy.addObserver(observer);
    dispatch({
      type: VideoTileGridAction.SetPriorityBasedPolicy,
      payload: { policy: priorityBasedPolicy },
    });
    return (): void => priorityBasedPolicy.removeObserver(observer);
  }, [audioVideo]);

  useEffect(() => {
    const localAttendeeId =
      meetingManager.meetingSession?.configuration.credentials?.attendeeId ||
      null;

    dispatch({
      type: VideoTileGridAction.UpdateLocalSourceState,
      payload: {
        isVideoEnabled,
        localAttendeeId,
        isLocalUserSharing,
        sharingAttendeeId,
      },
    });
  }, [
    isLocalUserSharing,
    isVideoEnabled,
    meetingManager.meetingSession,
    sharingAttendeeId,
  ]);

  useEffect(() => {
    dispatch({
      type: VideoTileGridAction.UpdateLayout,
      payload: {
        layout,
      },
    });
  }, [layout]);

  useEffect(() => {
    if (sharingAttendeeId && layout === Layout.Gallery) {
      setLayout(Layout.Featured);
      dispatch({
        type: VideoTileGridAction.UpdateLayout,
        payload: {
          layout,
        },
      });
    }
  }, [sharingAttendeeId]);

  const controls: Controls = {};

  return (
    <VideoTileGridStateContext.Provider value={state}>
      <VideoTileGridControlContext.Provider value={controls}>
        {children}
      </VideoTileGridControlContext.Provider>
    </VideoTileGridStateContext.Provider>
  );
};

const useVideoTileGridState = (): State => {
  const state = useContext(VideoTileGridStateContext);

  if (!state) {
    throw new Error(
      "useVideoTileGridState must be used within a VideoTileGridProvider"
    );
  }

  return state;
};
const useVideoTileGridControl = (): Controls => {
  const context = useContext(VideoTileGridControlContext);

  if (!context) {
    throw new Error(
      "useVideoTileGridControl must be used within VideoTileGridProvider"
    );
  }
  return context;
};
export {
  VideoTileGridProvider,
  useVideoTileGridState,
  useVideoTileGridControl,
};
